import {
	TFileId,
	ICustomerLanguageInfo,
	IMenuTranslation,
	IItemForGetShortInfo,
	IItemForGetShortInfoWithStatus,
	TDishType,
	ITranslationDetailedInfo,
	DirectionForPaginated,
	IRestaurantPropertiesModifierGroup,
	EntityStatus,
	TPortionSelectionMode,
	TPriceStrategy,
} from 'types';

export interface IModifierModifierGroups {
	id: string;
	modifierId: string;
	positionNumber: number;
	modifierGroupName: string | null;
}

export interface IModifierRestaurantModifiers {
	id: string;
	restaurantId: string;
	restaurantName: string | null;
	price: number | null;
	isDisabled: boolean;
	isRestricted: boolean;
	isPreselected: boolean;
	quantityMaxLimit: null | number;
	currencySymbol: string | null;
}

export interface IModifierCustomer {
	id: string;
	name: string | null;
}

export interface IItemModifier {
	id: string;
	name: Nullable<string>;
	filePath: Nullable<string>;
	description: Nullable<string>;
	customerId: string;
	externalId: Nullable<string>;
	isDisabled: boolean;
	isCombo: boolean;
	articleNumber: Nullable<string>;
	customer: IModifierCustomer;
	restaurantsModifiers: IModifierRestaurantModifiers[];
	modifierGroups: IModifierModifierGroups[];
}

// Menu v1
export interface IModifiers {
	modifiers: IItemModifier[];
	modifiersCount: number;
}
// Menu v1
// Menu v2
export interface IRestaurantWithModifierPrices {
	id: string;
	name: string;
	price: Nullable<number>;
	portionPrices: Nullable<number>[];
	currencySymbol: Nullable<string>;
}

export interface IModifiersByCustomerV2 {
	totalCount: number;
	items: {
		id: string;
		name: Nullable<string>;
		articleNumber: Nullable<string>;
		externalId: Nullable<string>;
		restaurants: IRestaurantWithModifierPrices[];
		preSignedUrl?: Nullable<string>;
	}[];
}

export interface IModifiersByRestaurantV2 {
	totalCount: number;
	items: {
		id: string;
		name: Nullable<string>;
		externalId: Nullable<string>;
		articleNumber: Nullable<string>;
		isDisabled: boolean;
		isRestricted: boolean;
		forceDisable: boolean;
		price: Nullable<number>;
		currencySymbol: Nullable<string>;
		portionPrices: Nullable<number>[];
		preSignedUrl?: Nullable<string>;
	}[];
}
// Menu v2

// Menu v1
export interface IGetPaginatedModifiers {
	pageNumber: number;
	pageSize: number;
	searchByName: string | null;
	customerId: string | null;
	restaurantId: string | null;
	modifierGroupId: string | null;
	priceFrom: number | null;
	priceTo: number | null;
	isDisabled: boolean;
	isRestricted: boolean;
	isShowOnlyComboItems: boolean;
	sortByName: string | null;
	sortByPrice: string | null;
}
// Menu v1

// Menu v2
export enum SortByForPaginatedModifiers {
	Name = 'Name',
}

export interface IGetPaginatedModifiersByCustomerV2 {
	customerId: string;
	pageNumber: number;
	pageSize: number;
	searchInput: Nullable<string>;
	sorting: Nullable<{
		sortBy: SortByForPaginatedModifiers;
		direction: DirectionForPaginated;
	}>;
}

export interface IGetPaginatedModifiersByRestaurantV2 {
	restaurantId: string;
	modifierGroupId: Nullable<string>;
	pageNumber: number;
	pageSize: number;
	searchInput: Nullable<string>;
	isDisabled: Nullable<boolean>;
	isRestricted: Nullable<boolean>;
	sorting: Nullable<{
		sortBy: SortByForPaginatedModifiers;
		direction: DirectionForPaginated;
	}>;
}
// Menu v2

export interface IRestaurantModifierForCreateModifier {
	restaurantId: string;
	price: number;
	isDisabled: boolean;
	isRestricted: boolean;
	isPreselected: boolean;
	quantityMaxLimit: null | number;
	forceDisable: boolean;
}
// Menu v1
export interface ICreateModifier {
	name: string;
	fileId: TFileId;
	description: string;
	customerId: string;
	restaurantsModifiers: IRestaurantModifierForCreateModifier[];
	modifierGroupIds: string[];
	activationCron: string | null;
	deactivationCron: string | null;
	translations: IMenuTranslation[];
}
// Menu v1

// Menu v2
export interface ICreateModifierV2 {
	name: string;
	description: Nullable<string>;
	customerId: string;
	fileId: TFileId;
	restaurantIds: string[];
	externalId: Nullable<string>;
	overrideScheduleAvailability: boolean;
	activationCron: Nullable<string>;
	deactivationCron: Nullable<string>;
	translations: IMenuTranslation[];
}
// Menu v2

// Menu v1
export interface IUpdateModifier extends ICreateModifier {
	id: string;
	externalId: string | null;
}
// Menu v1

// Menu v2
export interface IUpdateModifierV2 {
	id: string;
	name: string;
	description: Nullable<string>;
	externalId: Nullable<string>;
	fileId: TFileId;
	overrideScheduleAvailability: boolean;
	activationCron: Nullable<string>;
	deactivationCron: Nullable<string>;
	translations: IMenuTranslation[];
	restaurantIds: string[];
}
// Menu v2

// Menu v1
export interface IRestaurantModifierForGetInfo {
	id: string;
	restaurantId: string;
	price: number;
	isDisabled: boolean;
	isRestricted: boolean;
	isPreselected: boolean;
	restaurantName: string;
	restaurantStatus: EntityStatus;
	quantityMaxLimit: null | number;
	forceDisable: boolean;
}
// Menu v1

// Menu v2
export interface IGetModifierRestaurantProperties {
	data: {
		id: string;
		name: string;
		restaurantId: string;
		customer: IItemForGetShortInfo;
		price: Nullable<number>;
		currencySymbol: string;
		supplementPrice: Nullable<number>;
		isCreatedFromPos: boolean;
		isDisabled: boolean;
		isRestricted: boolean;
		isPreselected: boolean;
		forceDisable: boolean;
		quantityMaxLimit: Nullable<number>;
		activationCron: Nullable<string>;
		deactivationCron: Nullable<string>;
		parentModifierGroups: {
			id: string;
			name: string;
			isDefault: boolean;
			isAssignedToPortion: boolean;
		}[];
		childModifierGroups: IRestaurantPropertiesModifierGroup[];
		hasPortions: boolean;
		portionSelectionMode: TPortionSelectionMode;
		hasPortionBasedNestedModGroups: boolean;
	};
}
// Menu v2

export interface IGetModifierRestaurantPropertiesArguments {
	restaurantId: string;
	modifierId: string;
}

export interface IModifierGroupForEditModifier {
	id: string;
	modifierId: string;
	positionNumber: number;
	modifierGroupName: string;
}

// Menu v1
export interface IGetModifierInfo {
	data: {
		modifier: {
			id: string;
			preSignedUrl: Nullable<string>;
			filePath: Nullable<string>;
			name: string;
			description: string;
			customerId: string;
			customerName: string;
			externalId: Nullable<string>;
			isDisabled: boolean;
			isCombo: boolean;
			articleNumber: Nullable<string>;
			restaurantsModifiers: IRestaurantModifierForGetInfo[];
			modifierGroups: IModifierGroupForEditModifier[];
			activationCron: Nullable<string>;
			deactivationCron: Nullable<string>;
			customer: Nullable<ICustomerLanguageInfo>;
			translations: IMenuTranslation[];
		};
	};
}
// Menu v1

// Menu v2
export interface IGetModifierInfoV2 {
	data: {
		id: string;
		name: string;
		type: TDishType;
		photoPath: Nullable<string>;
		posName: Nullable<string>;
		description: Nullable<string>;
		externalId: Nullable<string>;
		articleNumber: Nullable<string>;
		activationCron: Nullable<string>;
		deactivationCron: Nullable<string>;
		overrideScheduleAvailability: boolean;
		isCreatedOnPos: boolean;
		customer: IItemForGetShortInfo;
		restaurants: IItemForGetShortInfoWithStatus[];
		restaurantsWithPortions: IItemForGetShortInfo[];
		translations: ITranslationDetailedInfo[];
	};
}
// Menu v2

export interface IModifierPositionItem {
	id: string;
	name: string | null;
	positionNumber: number;
	customer: { id: string; name: string };
	restaurants: { id: string; name: string; isDisabled: boolean; price: number }[];
}

export interface IGetModifierPositionList {
	data: {
		modifierPositions: IModifierPositionItem[];
	};
}

export interface IUpdateModifierPositions {
	modifierGroupId: string;
	modifierIds: string[];
}

export interface IUpdateModifierRestaurantProperties {
	restaurantId: string;
	modifierId: string;
	price: Nullable<number>;
	supplementPrice: Nullable<number>;
	quantityMaxLimit: Nullable<number>;
	isDisabled: boolean;
	isRestricted: boolean;
	isPreselected: boolean;
	forceDisable: boolean;
	activationCron: Nullable<string>;
	deactivationCron: Nullable<string>;
	portionSelectionMode: TPortionSelectionMode;
	modifierGroups: {
		id: string;
		upLimit: Nullable<number>;
		downLimit: Nullable<number>;
	}[];
}

export type TPropertiesOfPatchChangesForModifiers =
	| 'price'
	| 'supplementPrice'
	| 'quantityMaxLimit'
	| 'activation'
	| 'deactivation'
	| 'isDisabled'
	| 'isRestricted'
	| 'isPreselected'
	| 'forceDisable';

export interface IPropertiesOfPatchChangesForModifier {
	id: TPropertiesOfPatchChangesForModifiers;
	name: TPropertiesOfPatchChangesForModifiers;
}

export interface IUpdateRestaurantPropertiesAsPatchForModifier {
	modifierId: string;
	restaurantIds: string[];
	update: {
		price: {
			shouldUpdate: boolean;
			value: Nullable<number>;
		};
		supplementPrice: {
			shouldUpdate: boolean;
			value: Nullable<number>;
		};
		quantityMaxLimit: {
			shouldUpdate: boolean;
			value: Nullable<number>;
		};
		activationCron: {
			shouldUpdate: boolean;
			value: string;
		};
		deactivationCron: {
			shouldUpdate: boolean;
			value: string;
		};
		isDisabled: {
			shouldUpdate: boolean;
			value: boolean;
		};
		isRestricted: {
			shouldUpdate: boolean;
			value: boolean;
		};
		isPreselected: {
			shouldUpdate: boolean;
			value: boolean;
		};
		forceDisable: {
			shouldUpdate: boolean;
			value: boolean;
		};
	};
}

export interface IModifierPortionForGetInfo {
	id: string;
	name: string;
	posName: Nullable<string>;
	price: Nullable<number>;
	supplementPrice: Nullable<number>;
	currencySymbol: Nullable<string>;
	articleNumber: Nullable<string>;
	externalId: string;
	isProtectedFromSync: boolean;
	isDisabled: boolean;
	priceStrategy: TPriceStrategy;
	isCreatedFromPos: boolean;
	customer: IItemForGetShortInfo;
	restaurant: IItemForGetShortInfo;
	modifier: IItemForGetShortInfo;
	parentModifierGroups: IItemForGetShortInfo[];
	childModifierGroups: IItemForGetShortInfo[];
	translations: ITranslationDetailedInfo[];
}

export interface IGetModifierPortionInfo {
	data: IModifierPortionForGetInfo;
}

export interface IGetModifierRestaurantPortionsArguments {
	restaurantId: string;
	modifierId: string;
}

export interface IGetModifierRestaurantPortion {
	id: string;
	name: string;
	posName: Nullable<string>;
	price: Nullable<number>;
	supplementPrice: Nullable<number>;
	currencySymbol: string;
	articleNumber: Nullable<string>;
	externalId: Nullable<string>;
	isProtectedFromSync: boolean;
	isCreatedFromPos: boolean;
	isDisabled: boolean;
	priceStrategy: TPriceStrategy;
}

export interface IGetModifierRestaurantPortions {
	data: IGetModifierRestaurantPortion[];
}
